import React, { useEffect, useState } from "react";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  // FaGlobe,
  FaLinkedinIn,
  // FaGithub,
  FaYoutube,
  FaInstagram,
  // FaClock,
  FaPaperPlane,
  FaComments,
  FaUsers,
  FaHandshake,
  FaFacebookF,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

import "./ContactUs.css";

function ContactUs() {
  // =========================================================
  // FORM DATA
  // =========================================================

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  // =========================================================
  // CAPTCHA
  // =========================================================

  const [userCaptcha, setUserCaptcha] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [captchaStyles, setCaptchaStyles] = useState([]);
  const [captchaError, setCaptchaError] = useState("");

  // =========================================================
  // GENERATE CAPTCHA
  // =========================================================

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";

    const colors = [
      "#222222",
      "#444444",
      "#1d4ed8",
      "#b91c1c",
      "#15803d",
      "#7e22ce",
    ];

    let value = "";
    let styles = [];

    for (let i = 0; i < 6; i++) {
      const char = chars.charAt(Math.floor(Math.random() * chars.length));

      value += char;

      styles.push({
        color: colors[Math.floor(Math.random() * colors.length)],
        fontSize: `${20 + Math.floor(Math.random() * 10)}px`,
        transform: `
          rotate(${Math.floor(Math.random() * 31) - 15}deg)
          translateY(${Math.floor(Math.random() * 9) - 4}px)
        `,
      });
    }

    setCaptcha(value);
    setCaptchaStyles(styles);
    setUserCaptcha("");
  };

  // =========================================================
  // GENERATE CAPTCHA ON PAGE LOAD
  // =========================================================

  useEffect(() => {
    generateCaptcha();
  }, []);

  // =========================================================
  // SCROLL ANIMATION
  // =========================================================

  useEffect(() => {
    const animatedElements = document.querySelectorAll(".contact-animate");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("contact-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
      },
    );

    animatedElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // =========================================================
  // REFRESH CAPTCHA
  // =========================================================

  const refreshCaptcha = () => {
    generateCaptcha();
    setCaptchaError("");
  };

  // =========================================================
  // HANDLE INPUT CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // HANDLE FORM SUBMIT
  // =========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double click / duplicate request
    if (loading) {
      return;
    }

    // =======================================================
    // FRONTEND VALIDATION
    // =======================================================
    if (!formData.firstName?.trim()) {
      alert("Please enter your first name.");
      return;
    }

    if (!formData.lastName?.trim()) {
      alert("Please enter your last name.");
      return;
    }

    if (!formData.email?.trim()) {
      alert("Please enter your email.");
      return;
    }

    if (!formData.phone?.trim()) {
      alert("Please enter your phone number.");
      return;
    }

    if (!formData.subject?.trim()) {
      alert("Please select Product or Service.");
      return;
    }

    if (!formData.message?.trim()) {
      alert("Please enter your message.");
      return;
    }
    // =======================================================
    // CAPTCHA VALIDATION
    // =======================================================

    if (!userCaptcha.trim()) {
      setCaptchaError("Please enter the captcha.");
      return;
    }

    if (userCaptcha.trim().toUpperCase() !== captcha.toUpperCase()) {
      setCaptchaError("Invalid captcha.");

      // Generate a new CAPTCHA
      generateCaptcha();

      return;
    }

    setCaptchaError("");
    setLoading(true);

    try {
      // =====================================================
      // CONTACTUS API
      // =====================================================

      const apiUrl =
        "https://api-unsy-con-arins-39275.middleware.iconiqcrm.com/iconiqcrm/api/contact-us";

      // =====================================================
      // REQUEST DATA
      // =====================================================

      const requestData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        eid: "AVBA-G1791-O8794-D80909G-716-AAAA",
        buid: "UDCD-Y1791-I8795-A03228V-553-AAAA",
      };

      console.log("========================================");
      console.log("SENDING WEBSITE CONTACT");
      console.log("API URL:", apiUrl);
      console.log("Request Data:", requestData);
      console.log("========================================");

      // =====================================================
      // SEND REQUEST
      // =====================================================

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const responseText = await response.text();

      console.log("Contact API status:", response.status);
      console.log("Contact API response:", responseText);

      // Parse response safely
      let result = {};

      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch (error) {
        result = { message: responseText };
      }

      // =====================================================
      // HANDLE HTTP ERROR
      // =====================================================

      if (!response.ok) {
        console.error("========================================");
        console.error("CONTACT API ERROR");
        console.error("Status:", response.status);
        console.error("Response:", result);
        console.error("========================================");

        // 409 CONFLICT
        if (response.status === 409) {
          throw new Error(
            result.message || result.error || "This contact already exists.",
          );
        }

        // 400 BAD REQUEST
        if (response.status === 400) {
          throw new Error(
            result.message || result.error || "Invalid contact information.",
          );
        }

        // 500 SERVER ERROR
        if (response.status >= 500) {
          throw new Error(
            result.message ||
              result.error ||
              "Server error. Please try again later.",
          );
        }

        // OTHER ERRORS
        throw new Error(
          result.message ||
            result.error ||
            `Failed to submit contact form. Status: ${response.status}`,
        );
      }

      // =====================================================
      // SUCCESS
      // =====================================================

      console.log("========================================");
      console.log("WEBSITE CONTACT SAVED SUCCESSFULLY");
      console.log("Response:", result);
      console.log("========================================");

      alert("Thank you! Your message has been submitted successfully.");

      // =====================================================
      // CLEAR FORM
      // =====================================================

      setFormData({
        name: "",
        lastName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      // Clear CAPTCHA input
      setUserCaptcha("");

      // Generate fresh CAPTCHA
      generateCaptcha();
    } catch (error) {
      // =====================================================
      // ERROR
      // =====================================================

      console.error("========================================");
      console.error("CONTACT API ERROR");
      console.error("Error:", error);
      console.error("Message:", error?.message);
      console.error("========================================");

      alert(error?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* =========================
          Contact Hero Section
      ========================== */}

      <section className="contact-hero">
        <div className="contact-hero-content contact-animate">
          {/* Breadcrumb */}

          <div className="breadcrumb">Home &nbsp;›&nbsp; Contact</div>

          {/* Heading */}

          <h1>
            Let’s <span>Talk</span>
          </h1>

          <p>
            We’d love to hear from you! Get in touch with our team for any
            questions, support, or project inquiries.
          </p>

          {/* Highlights */}

          <div className="contact-highlights">
            {/* Quick Response */}

            <div className="highlight-item contact-animate contact-highlight-1">
              <span>
                <FaComments />
              </span>

              <div>
                <strong>Quick Response</strong>
                <small>We value your time</small>
              </div>
            </div>

            {/* Expert Consultation */}

            <div className="highlight-item contact-animate contact-highlight-2">
              <span>
                <FaUsers />
              </span>

              <div>
                <strong>Expert Consultation</strong>
                <small>Tailored to your needs</small>
              </div>
            </div>

            {/* Long-Term Partnership */}

            <div className="highlight-item contact-animate contact-highlight-3">
              <span>
                <FaHandshake />
              </span>

              <div>
                <strong>Long-Term Partnership</strong>
                <small>Growing together</small>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Decoration */}

        <div className="hero-decoration">
          <img src="/images/contact-illustration.png" alt="Contact Us" />
        </div>
      </section>

      {/* =========================
          Contact Main Section
      ========================== */}

      <section className="contact-main">
        {/* =========================
            Contact Form
        ========================== */}

        <div className="contact-form-card contact-animate">
          <h2>Send Us a Message</h2>

          <p>
            Fill out the form below and we'll get back to you as soon as
            possible.
          </p>

          <form onSubmit={handleSubmit}>
            {/* First Name + Last Name */}
            <div className="form-row">
              <div className="form-group">
                <label>
                  First Name <span>*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>
                  Last Name <span>*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email Address + Phone Number */}
            <div className="form-row">
              <div className="form-group">
                <label>
                  Email Address <span>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>
                  Phone Number <span>*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Want to Know About + Message */}
            <div className="form-row">
              <div className="form-group">
                <label>
                  Want to Know About <span>*</span>
                </label>

                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="" disabled>
                    Select Product or Service
                  </option>
                  <option value="Service">Service</option>
                  <option value="Product">Product</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  Message <span>*</span>
                </label>

                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  placeholder="Tell us about your requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* =========================
                CAPTCHA
            ========================== */}

            <div className="captcha-section">
              <label>
                Captcha <span>*</span>
              </label>

              <div className="captcha-row">
                {/* CAPTCHA Code */}

                <div className="captcha-code">
                  {captcha.split("").map((char, index) => (
                    <span key={index} style={captchaStyles[index]}>
                      {char}
                    </span>
                  ))}
                </div>

                {/* Refresh CAPTCHA */}

                <button
                  type="button"
                  className="captcha-refresh"
                  onClick={refreshCaptcha}
                  disabled={loading}
                  aria-label="Refresh CAPTCHA"
                >
                  ↻
                </button>

                {/* CAPTCHA Input */}

                <input
                  type="text"
                  value={userCaptcha}
                  onChange={(e) => {
                    setUserCaptcha(e.target.value);
                    setCaptchaError("");
                  }}
                  placeholder="Enter CAPTCHA"
                  disabled={loading}
                  autoComplete="off"
                />
              </div>

              {/* CAPTCHA Error */}

              {captchaError && (
                <span className="captcha-error">{captchaError}</span>
              )}
            </div>

            {/* =========================
                Send Button
            ========================== */}

            <button
              type="submit"
              className="send-btn"
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Tab" && !e.shiftKey && !loading) {
                  e.preventDefault();

                  document
                    .querySelector('.contact-form-card input[name="firstName"]')
                    ?.focus();
                }
              }}
            >
              <FaPaperPlane />

              <span>{loading ? "Sending..." : "Send Message"}</span>

              <strong>→</strong>
            </button>
          </form>
        </div>

        {/* =========================
            Contact Information
        ========================== */}

        <div className="contact-info contact-animate">
          <h2>Get in Touch</h2>

          {/* Location */}

          <div className="info-item contact-animate contact-info-1">
            <div className="info-icon">
              <FaMapMarkerAlt />
            </div>

            <div>
              <strong>Our Location</strong>

              <p>Thrissur, Kerala, India</p>
            </div>
          </div>

          {/* Phone */}

          <div className="info-item contact-animate contact-info-2">
            <div className="info-icon">
              <FaPhoneAlt />
            </div>

            <div>
              <strong>Call Us</strong>

              <p>+91 8891203931</p>
            </div>
          </div>

          {/* Email */}

          <div className="info-item contact-animate contact-info-3">
            <div className="info-icon">
              <FaEnvelope />
            </div>

            <div>
              <strong>Email Us</strong>

              <p>sales@mindstreamx.com</p>
            </div>
          </div>

          {/* Website

          <div className="info-item contact-animate contact-info-4">
            <div className="info-icon">
              <FaGlobe />
            </div>

            <div>
              <strong>Visit Our Website</strong>

              <p>www.mindstreamx.com</p>
            </div>
          </div> */}

          {/* Social Media */}

          <div className="social-section">
            <strong>Follow Us</strong>

            <div className="social-icons">
              <a
                href="https://www.linkedin.com/in/mindstreamx-technologies-243236445/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn />
              </a>

              <a
                href="https://www.facebook.com/profile.php?id=61595766561019"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebookF />
              </a>

              <a
                href="https://www.youtube.com/@yourchannel"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>

              <a
                href="https://www.instagram.com/mindstreamx.in/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>
              <a
                href="https://x.com/sudm29"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
              >
                <FaXTwitter />
              </a>
            </div>
          </div>

          {/* Google Map */}

          {/* <div className="map-box">
            <iframe
              title="MindstreamX Location"
              src="https://www.google.com/maps?q=Thrissur,Kerala,India&output=embed"
              loading="lazy"
            ></iframe>
          </div> */}

          {/* Working Hours */}

          {/* <div className="working-hours">
            <div className="clock-icon">
              <FaClock />
            </div>

            <div>
              <strong>Working Hours</strong>

              <p>Mon - Sat &nbsp; | &nbsp; 9:30 AM - 6:00 PM</p>

              <p>Sun &nbsp; : &nbsp; Closed</p>
            </div>
          </div> */}
        </div>
      </section>
    </div>
  );
}

export default ContactUs;
